// import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Grid,
  GridItem,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React from 'react';

import { Util } from '@finley-technologies/finley-types';

import { useGetValleyBankDownloadLinksQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { FiDownload } from 'react-icons/fi';
import { DownloadIconButton } from '~/packages/common-frontend/components/IconButton';
import { errorToastConfig } from '~/packages/common-frontend/constants';

type DownloadItemProps = {
  displayText: string;
  filename: string;
  url: string;
  loading: boolean;
};
const DownloadItem = ({
  displayText,
  filename,
  url,
  loading,
}: DownloadItemProps) => {
  const handleDownload = () => {
    if (loading) {
      return;
    }
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  return (
    <MenuItem>
      <Grid templateColumns="repeat(4, 1fr)" w="100%">
        <GridItem colSpan={3} textAlign="left">
          {displayText}
        </GridItem>
        <GridItem colSpan={1} textAlign="right">
          {loading ? (
            <Spinner
              thickness="1px"
              speed="0.65s"
              emptyColor="gray.200"
              color="blue.500"
              size="sm"
            />
          ) : (
            <DownloadIconButton
              isDisabled={!url}
              ariaLabel="download"
              label="Download"
              onClick={() => handleDownload()}
            />
          )}
        </GridItem>
      </Grid>
    </MenuItem>
  );
};

export const DownloadMenuList = ({
  reportingDate,
  isPreview,
}: {
  reportingDate: Util.FullDateStr;
  isPreview: boolean;
}) => {
  const toast = useToast();
  const { data } = useGetValleyBankDownloadLinksQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      reportingDate,
      isPreview,
    },
    fetchPolicy: 'network-only',
  });

  const downloadLinks = [
    {
      url:
        data?.valleyBankDownloadLinks.securitiesExtendedResponseTape?.url ?? '',
      filename: data?.valleyBankDownloadLinks.ingestionLogs?.filename ?? '',
      displayText: 'All Securities',
    },
    {
      url:
        data?.valleyBankDownloadLinks.depositsExtendedResponseTape?.url ?? '',
      filename:
        data?.valleyBankDownloadLinks.depositsExtendedResponseTape?.filename ??
        '',
      displayText: 'All Certificate of Deposit Accounts',
    },
    {
      url: data?.valleyBankDownloadLinks.loanAccountDetails?.url ?? '',
      filename:
        data?.valleyBankDownloadLinks.loanAccountDetails?.filename ?? '',
      displayText: 'All Loan Accounts',
    },
    {
      url: data?.valleyBankDownloadLinks.ingestionLogs?.url ?? '',
      filename: data?.valleyBankDownloadLinks.ingestionLogs?.filename ?? '',
      displayText: 'Ingestion Logs',
    },
    {
      url: data?.valleyBankDownloadLinks.collateralAssignments?.url ?? '',
      filename:
        data?.valleyBankDownloadLinks.collateralAssignments?.filename ?? '',
      displayText: 'Collateral Assignments',
    },
  ];

  return (
    <MenuList>
      {downloadLinks.map(({ url, filename, displayText }) => (
        <DownloadItem
          key={displayText}
          loading={!data}
          url={url}
          filename={filename}
          displayText={displayText}
        />
      ))}
    </MenuList>
  );
};

export const DownloadMenuButton = ({
  reportingDate,
  buttonLabel,
  isPreview,
}: {
  reportingDate: Util.FullDateStr;
  buttonLabel?: string;
  isPreview: boolean;
}) => {
  // Utilize Chakra's useDisclosure hook to manage the open state of the menu
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <Box>
      <Menu isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
        <MenuButton
          as={Button}
          leftIcon={<FiDownload />}
          variant="ghost"
          border="2px solid"
          onClick={() => !isOpen && onOpen()} // Ensures onOpen is called when the menu is not already open
        >
          {buttonLabel || 'Download'}
        </MenuButton>
        {isOpen && (
          <DownloadMenuList
            reportingDate={reportingDate}
            isPreview={isPreview}
          />
        )}
      </Menu>
    </Box>
  );
};
